// session-preflight-hook-file-checks.ts
//
// "Does every hook command declared in settings.json point at a file that
// exists?" — split out of session-preflight-checks.ts (plan 20260924-0205,
// 200-line budget). Behaviour unchanged. Note this check cannot fail on an
// EMPTY hook list; session-preflight-guard-active-checks.ts asks that question.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists } from "../filesystem-helpers.js";
import type { PreflightResult, SettingsHooksLike } from "./session-preflight-types.js";

// Resolves a hook `command` string that may reference `${CLAUDE_PROJECT_DIR}`
// against the mission workspace root (that's what the real hooks do — Claude
// Code sets CLAUDE_PROJECT_DIR to the workspace cwd). Any other relative path
// is checked as-is relative to missionWorkspace; absolute paths checked
// as-is.
//
// D06 finding A (real S1 smoke): this used to split the FULLY-RESOLVED
// command string (i.e. after substituting missionWorkspace into
// ${CLAUDE_PROJECT_DIR}) on whitespace. If missionWorkspace itself contains a
// space, the resolved path gets truncated at that space and never matches
// anything on disk -> preflight blocks unconditionally for any mission
// workspace whose path has a space. Fix: tokenize the ORIGINAL template on
// whitespace FIRST (while it still contains the literal, space-free
// "${CLAUDE_PROJECT_DIR}" placeholder token), find the path-shaped token,
// THEN substitute the placeholder in that single token — so an embedded
// space in missionWorkspace stays inside one token instead of splitting it.
//
// N-01 (audit remediation 2026-09-14): D06 only covered the template form. A
// LITERAL path with an embedded space (`node /tmp/a b/hook.cjs`) was still
// split at the space and truncated to `/tmp/a` — measured as
// `details: "/…/T/a"` from the real `/…/T/a b/…`. For the non-template branch
// we now extend the candidate one token at a time and take the first that
// exists on disk. Runs of whitespace collapse to a single space (accepted).
// Nothing exists ⇒ return the single-token candidate so the failure reason and
// details stay what they were.
async function resolveHookCommandPath(
  command: string,
  missionWorkspace: string,
): Promise<string | undefined> {
  const tokenPattern = "${CLAUDE_PROJECT_DIR}";
  // hook `command` strings are often `node <path> <args...>` — take the
  // first whitespace-separated token (from the UNRESOLVED template) that
  // looks like a path: contains "/" or is the ${CLAUDE_PROJECT_DIR} token
  // itself.
  const tokens = command.split(/\s+/).filter(Boolean);
  const candidateIndex = tokens.findIndex((t) => t.includes("/") || t.includes(tokenPattern));
  if (candidateIndex === -1) return undefined;
  const candidateToken = tokens[candidateIndex] as string;
  const toPath = (raw: string): string => (raw.startsWith("/") ? raw : join(missionWorkspace, raw));

  // Template branch: unchanged from D06 — substitute inside the single token.
  if (candidateToken.includes(tokenPattern)) {
    return toPath(candidateToken.replace(tokenPattern, missionWorkspace));
  }

  const first = toPath(candidateToken);
  if (await pathExists(first)) return first;
  for (let end = candidateIndex + 2; end <= tokens.length; end++) {
    const joined = toPath(tokens.slice(candidateIndex, end).join(" "));
    if (await pathExists(joined)) return joined;
  }
  return first;
}

export async function checkHookCommandsExist(missionWorkspace: string): Promise<PreflightResult> {
  if (!missionWorkspace) throw new Error("checkHookCommandsExist: missionWorkspace là bắt buộc.");

  const settingsPath = join(missionWorkspace, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) {
    return { ok: false, reason: "settings_missing", details: settingsPath };
  }

  let settings: SettingsHooksLike;
  try {
    settings = JSON.parse(await fs.readFile(settingsPath, "utf8")) as SettingsHooksLike;
  } catch (err) {
    return {
      ok: false,
      reason: "settings_malformed_json",
      details: err instanceof Error ? err.message : String(err),
    };
  }

  const missing: string[] = [];
  for (const matchers of Object.values(settings.hooks ?? {})) {
    for (const matcher of matchers) {
      for (const hookEntry of matcher.hooks ?? []) {
        if (!hookEntry.command) continue;
        const resolved = await resolveHookCommandPath(hookEntry.command, missionWorkspace);
        if (!resolved) continue; // command has no discernible file path token
        if (!(await pathExists(resolved))) missing.push(resolved);
      }
    }
  }

  if (missing.length > 0) {
    return { ok: false, reason: "hook_command_file_missing", details: missing.join(", ") };
  }
  return { ok: true };
}
